// src/pages/misc/LandingPage.tsx
// Marketing landing (/landing) — nine-beat path from ventaje-landing-v2.html.
// Background: ClassicLens (App). Glass: exp-landing-* bands. Primary CTA: btn-gold.
//
// [MODULE-MANIFEST] See continuousPath.ts (pages/misc/onboarding) for the full list of routes, imports and storage keys.
// [HANDOFF] THE NINE BEATS, top to bottom (each <section> has an id, a data-beat name, and is tracked in beatRefs for the thread/menu):
//   1 #top        Hero: headline, two buttons ("Encuentra tu ventaja" scrolls to #fork, "Mira cómo funciona" opens a video), two phone-style previews.
//   2 #fork       "¿Quién eres?": two cards (owner / agent). Hover or tap sets `path`, which changes the COPY of beats 3, 6 and 8. Soft pin.
//   3 #c1         Contrast 1 "¿Vendes o capturas?": pinned. "Hoy" card blurs out as the "Con Ventaje" card comes in (scrubPin).
//   4 #statement  Black band: the words of "No te faltan leads. Te falta tiempo..." light up one by one while you scroll.
//   5 #ia, #dial  AI contrast (pinned), then the dial: five roles (Secretaria ... Socia) with a needle driven by scroll or by dragging.
//   6 #money      Contrast "La cuenta": pinned, last of the contrasts (data-last: its right card gets a quiet extra lift instead of fading).
//   7 #conseq     Black band "Ahora imagina seguir igual un año más": three lines fade in once, when 30% visible.
//   8 #cierre     Close. Owner: pinned; three promises type themselves while scrolling, then the "Empezar" button. Agent: plain glass band.
//   9 #cuestionario  First setup question (LandingPathStep). Its Continuar goes to /onboarding on the next question.
// [HANDOFF] Where the landing leaves to: /onboarding (top "Empezar", and the last section), /socios (fork link, close for agents,
// [URL-RENAME] -> /partners), /contacto (the floating WhatsApp bubble, [URL-RENAME] -> /contact), and the menu (LandingMenu.tsx).
// The landing hash #cuestionario is read on load and scrolls there (used by the builder's "Atrás"). [URL-RENAME] -> #quiz
// [I18N] All text on this page is typed Spanish in the code. The `lang` state only feeds the menu's language switch (nothing translates yet).
// [ANIMATION] ONE scroll loop (the big useEffect below) drives everything with requestAnimationFrame: thread, background drift, active beat,
// pins, words, fork, dial, close. Nothing uses a scroll library. With prefers-reduced-motion, pins/words/dial/close effects are skipped
// and the content just shows in place.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck,
  DatabaseZap,
  FileText,
  MessageCircle,
  PhoneCall,
  Play,
  Rocket,
  Stethoscope,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { LandingHeroPair } from './landing/HeroPreviews';
import LandingMenu from './landing/LandingMenu';
import LandingPathStep from './landing/LandingPathStep';
import ContrastVisual from './landing/contrast';
import {
  LandingVideoModal,
  VIDEOS,
  type LandingVideo,
  type LandingVideoKey,
} from './landing/LandingVideoModal';
import './landing/landing.css';
// PathKind: his LandingQuiz.tsx is not ported (docs/manifest/WU-147.md SESSION 0 RECORD S-10); its l.11 type lives here
type PathKind = 'owner' | 'agent';

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const smoothstep = (t: number) => t * t * (3 - 2 * t);

// [ANIMATION] Length of the dial's half-circle arc: pi x radius 120 = 377. The fill is drawn by stroke-dashoffset = DIAL_LEN x (1 - progress).
// The needle knob sits on the same circle: center (150,150), radius 120, angle = pi x (1 - progress) (left end = 0, right end = 1).
const DIAL_LEN = 377;

// Shortest the hand-drawn scrollbar thumb gets, so it stays visible on this very tall page.
const SCROLL_THUMB_MIN = 72;

// Same roles as the app's automation dial (AutomationDial.tsx), in order.
const DIAL_PHASES: { role: string; bullets: string[]; icon: LucideIcon }[] = [
  {
    role: 'Secretaria',
    icon: FileText,
    bullets: ['Resume cada conversación', 'Llena la ficha: presupuesto, zona, tiempos'],
  },
  {
    role: 'Asistente',
    icon: Workflow,
    bullets: ['Mueve a cada lead a su etapa', 'Te marca los que necesitan atención'],
  },
  {
    role: 'Suplente',
    icon: MessageCircle,
    bullets: ['Responde precio, ubicación, disponibilidad', 'Lo complejo te lo deja a ti'],
  },
  {
    role: 'Coordinadora',
    icon: CalendarCheck,
    bullets: ['Agenda visitas y llamadas', 'Registra leads y confirma por ti'],
  },
  {
    role: 'Socia',
    icon: PhoneCall,
    bullets: ['Hace la primera llamada… con tu voz', 'Reactiva leads callados', 'Tú solo cierras'],
  },
];

// Needle stays on Secretaria until the dial has settled, then turns across the long hold.
// The section is tall on purpose so this sweep is slow (about 40% of the old speed).
const DIAL_SWEEP_START = 0.28;
const DIAL_SWEEP_END = 0.64;

// [ANIMATION] Paints the dial for progress p (0..1): fill length, knob position, and which of the five phases is "on"
// (phase = floor(p x 5), so each role owns a fifth of the arc). Also sets aria-valuenow / aria-valuetext for screen readers.
const paintDial = (
  p: number,
  fill: SVGPathElement | null,
  knob: SVGCircleElement | null,
  phases: (HTMLDivElement | null)[],
  svg: SVGSVGElement | null,
) => {
  if (fill) fill.style.strokeDashoffset = String(DIAL_LEN * (1 - p));
  if (knob) {
    const ang = Math.PI * (1 - p);
    knob.setAttribute('cx', String(150 + 120 * Math.cos(ang)));
    knob.setAttribute('cy', String(150 - 120 * Math.sin(ang)));
  }
  const phase = Math.min(DIAL_PHASES.length - 1, Math.floor(p * DIAL_PHASES.length));
  phases.forEach((el, i) => el?.classList.toggle('on', i === phase));
  if (svg) {
    svg.setAttribute('aria-valuenow', String(Math.round(p * 100)));
    svg.setAttribute('aria-valuetext', DIAL_PHASES[phase].role);
  }
};

// Owner close: three promises that type themselves out in order.
const CLOSE_STEPS: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: DatabaseZap,
    title: 'Migración y configuración gratis',
    body: 'Pasamos a tus asesores, contactos y leads.',
  },
  {
    icon: Stethoscope,
    title: 'Diagnóstico gratis',
    body: 'Te mostramos dónde se enfrían tus leads.',
  },
  {
    icon: Rocket,
    title: 'Piloto de 2 semanas',
    body: 'Con 2–3 asesores, con números de antes y después.',
  },
];

// Smooth-scrolls to a CSS selector (instant when the visitor prefers reduced motion). Used by the menu and the buttons.
const scrollToSel = (sel: string, reduce: boolean) => {
  document.querySelector(sel)?.scrollIntoView({
    behavior: reduce ? 'auto' : 'smooth',
    block: 'start',
  });
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  // [HANDOFF] 'owner' (runs a team) or 'agent' (sells alone). Only changes copy on this page; it is NOT saved and NOT the same as the
  // onboarding's 3-way path (draft.path). The onboarding question is separate (LandingPathStep, beat 9).
  const [path, setPath] = useState<PathKind>('owner');
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useState<'es' | 'en'>('es');
  const [topSolid, setTopSolid] = useState(false);
  const [waOn, setWaOn] = useState(false);
  const [video, setVideo] = useState<LandingVideo | null>(null);
  const closeVideo = useCallback(() => setVideo(null), []);
  const [activeBeat, setActiveBeat] = useState(0);
  const [conseqOn, setConseqOn] = useState(false);

  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const scrollDragRef = useRef<{ y: number; scroll: number } | null>(null);
  const reduceRef = useRef(false);
  const threadFillRef = useRef<HTMLDivElement>(null);
  const bgDetailRef = useRef<SVGSVGElement>(null);
  const dialFillRef = useRef<SVGPathElement>(null);
  const dialKnobRef = useRef<SVGCircleElement>(null);
  const dialSvgRef = useRef<SVGSVGElement | null>(null);
  const dialScrollPRef = useRef(0);
  const dialManualRef = useRef<{ p: number; base: number } | null>(null);
  const dialDragRef = useRef(false);
  const closeRef = useRef<HTMLElement | null>(null);
  const pinRefs = useRef<(HTMLElement | null)[]>([]);
  const wordSpansRef = useRef<(HTMLSpanElement | null)[]>([]);
  const beatRefs = useRef<(HTMLElement | null)[]>([]);
  const conseqRef = useRef<HTMLDivElement>(null);
  const qSecRef = useRef<HTMLElement | null>(null);
  const c1Ref = useRef<HTMLElement | null>(null);
  const statementRef = useRef<HTMLElement | null>(null);
  const dialSecRef = useRef<HTMLElement | null>(null);
  const phaseRefs = useRef<(HTMLDivElement | null)[]>([]);
  const forkSecRef = useRef<HTMLElement | null>(null);

  const openMenu = () => setMenuOpen(true);
  const closeMenu = () => setMenuOpen(false);

  const go = (sel: string) => {
    closeMenu();
    scrollToSel(sel, reduceRef.current);
  };

  // Reveal + consequence observers
  // [ANIMATION] Any element with .vl-reveal gets the class `in` the first time 15% of it is visible (then it is no longer observed);
  // the CSS in landing.css does the fade/rise. The "consequence" block turns on once when 30% visible.
  useEffect(() => {
    reduceRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const revealIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            revealIo.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 },
    );
    document.querySelectorAll('.ventaje-landing .vl-reveal').forEach((el) => revealIo.observe(el));

    const conseq = conseqRef.current;
    let cio: IntersectionObserver | undefined;
    if (conseq) {
      cio = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              setConseqOn(true);
              cio?.unobserve(e.target);
            }
          });
        },
        { threshold: 0.3 },
      );
      cio.observe(conseq);
    }

    return () => {
      revealIo.disconnect();
      cio?.disconnect();
    };
  }, []);

  // Body lock when menu open
  useEffect(() => {
    document.body.classList.toggle('locked', menuOpen);
    const root = document.querySelector('.ventaje-landing');
    root?.classList.toggle('locked', menuOpen);
    return () => {
      document.body.classList.remove('locked');
    };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // [HANDOFF] Arriving with #cuestionario (e.g. "Atrás" from the builder) scrolls to the quiz. It retries up to 12 times, 80ms apart,
  // because pinned sections above change the page height while they lay out; it only nudges while the quiz is still lower than 40px from the top.
  useEffect(() => {
    if (window.location.hash !== '#cuestionario') return;
    let tries = 0;
    let timer = 0;
    const tick = () => {
      const el = document.getElementById('cuestionario');
      if (el && el.getBoundingClientRect().top > 40) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
      tries += 1;
      if (tries < 12) timer = window.setTimeout(tick, 80);
    };
    timer = window.setTimeout(tick, 80);
    return () => window.clearTimeout(timer);
  }, []);

  // Scroll loop — thread, pins, dial, words, constellation
  useEffect(() => {
    // The browser's own bar is hidden on the landing; .vl-scrollbar draws the onboarding's thin glowing line instead.
    document.documentElement.classList.add('vl-no-native-bar');
    let ticking = false;
    const reduce = () => reduceRef.current;

    // [ANIMATION] HOW A "PIN" WORKS. A pinned section is a tall <section> (extra scroll height on purpose) holding a position:sticky
    // `.vl-pin-inner`. While the section scrolls past, the inner block stays on screen. total = sectionHeight - viewportHeight is the
    // distance you can scroll while it is stuck. The tall heights are set in landing.css (see the PIN HEIGHTS list in landing.css).
    // Pin progress 0→1 across the sticky range (0 = just pinned, 1 = about to release).
    const pinProgress = (sec: HTMLElement) => {
      const h = window.innerHeight;
      const total = sec.offsetHeight - h;
      return { p: clamp(-sec.getBoundingClientRect().top / Math.max(total, 1), 0, 1), total, h };
    };

    // Blended pin motion. The body arrives at normal scroll speed and eases to a
    // near-stop (speed-matched, so the pin never "grabs"), drifts slowly through the
    // hold, then eases back up to scroll speed before release. No hard track switch.
    // ease = how much of the hold is spent slowing down / speeding up;
    // floor = scroll speed kept during the hold (never a full stop).
    const movePinBody = (
      sec: HTMLElement,
      p: number,
      total: number,
      ease = 0.24,
      floor = 0.11,
    ) => {
      const body = sec.querySelector<HTMLElement>('.vl-pin-body');
      if (!body) return;
      const d = ((1 - floor) * ease * total) / 2; // speed-matched at both seams
      let y = -floor * total * p;
      if (p < ease) {
        const u = 1 - p / ease;
        y += d * u * u;
      } else if (p > 1 - ease) {
        const u = (p - (1 - ease)) / ease;
        y -= d * u * u;
      }
      body.style.transform = `translate3d(0,${y.toFixed(2)}px,0)`;
    };

    // Soft pins (band shorter than the viewport): after release the band has travelled
    // up by its ease + hold distance, leaving dead space under it. Pull the next section
    // up by that amount (minus a little breathing room) so there is no empty runway.
    const trimSoftTail = (
      sec: HTMLElement,
      total: number,
      h: number,
      ease: number,
      floor: number,
      air = 56,
    ) => {
      const body = sec.querySelector<HTMLElement>('.vl-pin-body');
      if (!body) return;
      const d = ((1 - floor) * ease * total) / 2;
      const gap = (h - body.offsetHeight) / 2 + d + floor * total;
      const mb = `${-Math.max(0, Math.round(gap - air))}px`;
      if (sec.style.marginBottom !== mb) sec.style.marginBottom = mb;
    };

    // [ANIMATION] CONTRAST SWAP (beats 3, 5-ia, 6). e = smoothstep((p - 0.05) / 0.58): 0 = only the "Hoy" card, 1 = only "Con Ventaje".
    //   "Hoy" (data-no):  opacity 1 -> 0.28, blur 0 -> 7px, moves up 26px, scales 1 -> 0.92.
    //   "Con Ventaje" (data-si): blur 7px -> 0, rises from 30px low, scale 0.94 -> 1.02, opacity 0.18 -> 1. Whichever is on top has z-index 3.
    //   The glow class vl-si-glow turns on at e > 0.55. "Ver más información" button fades/scales in across the visit (see below).
    //   --vl-swap (e) and data-swap ("no" | "si") are exposed on the section so the pictures in landing/contrast can animate from CSS.
    const scrubPin = (sec: HTMLElement, isLast: boolean) => {
      const { p, total, h } = pinProgress(sec);
      movePinBody(sec, p, total);
      const no = sec.querySelector<HTMLElement>('[data-no]');
      const si = sec.querySelector<HTMLElement>('[data-si]');
      if (!no || !si) return;
      // Swap starts while still decelerating and finishes as speed picks back up.
      const t = clamp((p - 0.05) / 0.58, 0, 1);
      const e = smoothstep(t);
      no.style.opacity = String(1 - 0.72 * e);
      no.style.filter = `blur(${7 * e}px)`;
      no.style.transform = `translate3d(0,${-26 * e}px,0) scale(${1 - 0.08 * e})`;
      no.style.zIndex = e < 0.5 ? '3' : '1';
      si.style.filter = `blur(${7 * (1 - e)}px)`;
      let pop = e * 1.02 + (1 - e) * 0.94;
      if (isLast && p > 0.78) {
        // Last contrast: a quiet extra lift instead of fading to black.
        pop = 1.02 + 0.05 * smoothstep(clamp((p - 0.78) / 0.22, 0, 1));
      }
      si.style.transform = `translate3d(0,${30 * (1 - e)}px,0) scale(${pop})`;
      si.style.opacity = String(0.18 + 0.82 * e);
      si.style.zIndex = e < 0.5 ? '1' : '3';
      const card = si.querySelector('.vl-card');
      card?.classList.toggle('vl-si-glow', e > 0.55);

      // "More info" runs the whole visit: it starts before the left card has settled
      // and finishes only after the right card is already in.
      const more = sec.querySelector<HTMLElement>('[data-more]');
      if (more) {
        const top = sec.getBoundingClientRect().top;
        const m = smoothstep(clamp((h - top) / (h + total * 0.92), 0, 1));
        const scale = m < 0.65 ? 0.88 + (m / 0.65) * 0.2 : 1.08 - ((m - 0.65) / 0.35) * 0.08;
        more.style.top = `${no.offsetHeight + 18}px`;
        more.style.opacity = String(m);
        more.style.transform = `translate3d(0,${((1 - m) * 18).toFixed(1)}px,0) scale(${scale.toFixed(3)})`;
        more.style.pointerEvents = m > 0.55 ? 'auto' : 'none';
      }

      // Bridge for the swappable visuals (landing/contrast): they animate off these in CSS.
      sec.style.setProperty('--vl-swap', e.toFixed(3));
      const swapState = e > 0.5 ? 'si' : 'no';
      if (sec.dataset.swap !== swapState) sec.dataset.swap = swapState;
    };

    const onScroll = () => {
      const y = window.scrollY;
      const h = window.innerHeight;
      const doc = document.documentElement.scrollHeight - h;
      const thumb = scrollThumbRef.current;
      if (thumb) {
        const thumbH = Math.max(SCROLL_THUMB_MIN, (h * h) / (doc + h));
        thumb.style.height = `${thumbH}px`;
        thumb.style.transform = `translate3d(0,${(clamp(y / Math.max(doc, 1), 0, 1) * (h - thumbH)).toFixed(1)}px,0)`;
      }
      // [ANIMATION] LEFT THREAD: its height % = page scroll progress. (The builder's thread is this same bar, flipped to grow from the bottom.)
      if (threadFillRef.current) {
        // Fills with the page and stays full once you reach the bottom.
        const pct = clamp(y / Math.max(doc, 1), 0, 1);
        threadFillRef.current.style.height = `${pct * 100}%`;
      }
      // [ANIMATION] The top bar turns solid after 40px of scroll. The faint constellation lines drift slowly with scroll:
      // x = -0.012 x scrollY, y = -0.03 x scrollY (px).
      setTopSolid(y > 40);
      if (!reduce() && bgDetailRef.current) {
        bgDetailRef.current.style.transform = `translate3d(${-y * 0.012}px,${-y * 0.03}px,0)`;
      }

      // The "active beat" is the last section whose top has passed 55% of the viewport height. It is written to data-active-beat on the root.
      let active = 0;
      beatRefs.current.forEach((s, i) => {
        if (s && s.getBoundingClientRect().top <= h * 0.55) active = i;
      });
      setActiveBeat(active);

      const qSec = qSecRef.current;
      const c1 = c1Ref.current;
      // [ANIMATION] The floating WhatsApp bubble (bottom-right, goes to /contacto) is shown only from the first contrast (#c1 above 50% of the
      // screen) until the closing question section (#cuestionario) comes within 60% of the screen.
      if (qSec) {
        const qr = qSec.getBoundingClientRect();
        if (c1) {
          setWaOn(c1.getBoundingClientRect().top < h * 0.5 && qr.top > h * 0.6);
        }
      }

      if (!reduce()) {
        pinRefs.current.forEach((sec) => {
          if (sec) scrubPin(sec, sec.hasAttribute('data-last'));
        });

        // [ANIMATION] STATEMENT WORDS (beat 4): p = (0.85 x viewport - blockTop) / (0.8 x blockHeight), clamped. The first round(p x wordCount)
        // words get the class `on` (they light up from left to right as the band rises). Words are 4 + 7 = 11 spans; the second line is `.key`.
        const statement = statementRef.current;
        const spans = wordSpansRef.current.filter(Boolean) as HTMLSpanElement[];
        if (statement && spans.length) {
          const r = statement.getBoundingClientRect();
          const p = clamp((h * 0.85 - r.top) / (r.height * 0.8), 0, 1);
          const n = Math.round(p * spans.length);
          spans.forEach((s, i) => s.classList.toggle('on', i < n));
        }

        // Fork: barely-there hold so the two paths can be hovered/picked.
        const forkSec = forkSecRef.current;
        if (forkSec) {
          const fInner = forkSec.querySelector<HTMLElement>('.vl-pin-inner');
          const fp = pinProgress(forkSec);
          if (fInner && getComputedStyle(fInner).position === 'sticky' && fp.total > h * 0.3) {
            movePinBody(forkSec, fp.p, fp.total, 0.4, 0.35);
            // Negative air pulls the next section (the "vende o captura" contrast) up closer.
            trimSoftTail(forkSec, fp.total, h, 0.4, 0.35, -100);
          } else {
            forkSec.style.marginBottom = '';
          }
        }

        const dialSec = dialSecRef.current;
        const dialFill = dialFillRef.current;
        const knob = dialKnobRef.current;
        if (dialSec && dialFill && knob) {
          let p: number;
          const inner = dialSec.querySelector<HTMLElement>('.vl-pin-inner');
          const pinned = !!inner && getComputedStyle(inner).position === 'sticky';
          const soft = pinProgress(dialSec);
          if (pinned && soft.total > h * 0.4) {
            // Soft pin: slows early and holds longer so all five phases can be read.
            // Full stop in the middle so the whole cycle can be watched.
            movePinBody(dialSec, soft.p, soft.total, 0.32, 0);
            // Negative air: the band's bottom tucks ~40px under the black section that follows.
            trimSoftTail(dialSec, soft.total, h, 0.32, 0, -40);
            // Starts turning once the dial is well inside the viewport; hits Máximo
            // just past the middle of the hold.
            // Slow sweep across the held dial: Secretaria once it has settled, Máximo before it leaves.
            p = clamp(
              (soft.p - DIAL_SWEEP_START) / (DIAL_SWEEP_END - DIAL_SWEEP_START),
              0,
              1,
            );
          } else {
            dialSec.style.marginBottom = '';
            // Not pinned (mobile / reduced motion): fill as the section enters.
            const r = dialSec.getBoundingClientRect();
            p = clamp((h * 0.9 - r.top) / (h * 0.9), 0, 1);
          }
          // Scroll drives the dial unless the visitor grabbed it; a real scroll hands it back.
          dialScrollPRef.current = p;
          const manual = dialManualRef.current;
          if (manual) {
            if (!dialDragRef.current && Math.abs(p - manual.base) > 0.02) dialManualRef.current = null;
            else p = manual.p;
          }
          paintDial(p, dialFill, knob, phaseRefs.current, dialSvgRef.current);
        }

        // Close steps: each one pops into place, then types, as you scroll. One, then two, then three.
        // [ANIMATION] TYPED CLOSE (owner only). cp = 0 when the top of the steps list reaches the bottom of the screen, 1 when the pin
        //   releases, so typing starts as soon as the steps come into view. Step i starts at cp = i x 0.12 and takes 0.2 of progress (slot 0..1):
        //   it shows (class `in`) once slot > 0.04, and types after slot 0.16: n = round(typed x (titleLength + bodyLength)) characters,
        //   title first, then body. The caret is shown on whichever of title/body is mid-typing. The "Empezar" button fades in at cp > 0.58.
        //   Text is set with textContent every frame, so the full strings must stay in CLOSE_STEPS (not only in the JSX).
        const close = closeRef.current;
        if (close) {
          const cInner = close.querySelector<HTMLElement>('.vl-pin-inner');
          const pinned = !!cInner && getComputedStyle(cInner).position === 'sticky';
          const stepEls = close.querySelectorAll<HTMLElement>('.vl-step');
          const stepsBox = close.querySelector<HTMLElement>('.vl-steps');
          if (pinned && cInner && stepsBox && stepEls.length) {
            const { total, h } = pinProgress(close);
            // Distance from the top of the pinned frame to the steps list (the same before and during the pin).
            const offset = stepsBox.getBoundingClientRect().top - cInner.getBoundingClientRect().top;
            const travelled = h - offset - close.getBoundingClientRect().top;
            const cp = clamp(travelled / Math.max(total + h - offset, 1), 0, 1);
            stepEls.forEach((stepEl, i) => {
              const meta = CLOSE_STEPS[i];
              if (!meta) return;
              const slot = clamp((cp - i * 0.12) / 0.2, 0, 1);
              const show = slot > 0.04;
              if (stepEl.classList.contains('in') !== show) stepEl.classList.toggle('in', show);
              const typed = clamp((slot - 0.16) / 0.84, 0, 1);
              const total = meta.title.length + meta.body.length;
              const n = Math.round(typed * total);
              const titleEl = stepEl.querySelector<HTMLElement>('[data-step-title]');
              const bodyEl = stepEl.querySelector<HTMLElement>('[data-step-body]');
              const titleText = meta.title.slice(0, n);
              const bodyText = meta.body.slice(0, Math.max(0, n - meta.title.length));
              if (titleEl && titleEl.textContent !== titleText) titleEl.textContent = titleText;
              if (bodyEl && bodyEl.textContent !== bodyText) bodyEl.textContent = bodyText;
              const titleCaret = stepEl.querySelector<HTMLElement>('[data-caret="title"]');
              const bodyCaret = stepEl.querySelector<HTMLElement>('[data-caret="body"]');
              if (titleCaret) titleCaret.hidden = !(n > 0 && n < meta.title.length);
              if (bodyCaret) bodyCaret.hidden = !(n >= meta.title.length && n < total);
            });
            const goBtn = close.querySelector<HTMLElement>('.vl-empezar');
            goBtn?.classList.toggle('in', cp > 0.58);
          }
        }
      }

      ticking = false;
    };

    const onScrollRaf = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(onScroll);
      }
    };

    const onResize = () => onScroll();
    onScroll();
    window.addEventListener('scroll', onScrollRaf, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      document.documentElement.classList.remove('vl-no-native-bar');
      window.removeEventListener('scroll', onScrollRaf);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Click or drag the dial to move it by hand, like the dial inside the app.
  const dialPFromPointer = (e: React.PointerEvent) => {
    const svg = dialSvgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 300;
    const y = ((e.clientY - rect.top) / rect.height) * 170;
    const dx = x - 150;
    const dy = 150 - y;
    if (dy <= 0) return dx < 0 ? 0 : 1;
    return clamp(1 - Math.atan2(dy, dx) / Math.PI, 0, 1);
  };
  const grabDial = (hitP: number) => {
    dialManualRef.current = { p: hitP, base: dialScrollPRef.current };
    paintDial(hitP, dialFillRef.current, dialKnobRef.current, phaseRefs.current, dialSvgRef.current);
  };
  const onDialDown = (e: React.PointerEvent<SVGSVGElement>) => {
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* Pointer can already be gone; the drag still works. */
    }
    dialDragRef.current = true;
    grabDial(dialPFromPointer(e));
  };
  const onDialMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!dialDragRef.current) return;
    const hitP = dialPFromPointer(e);
    if (dialManualRef.current) dialManualRef.current.p = hitP;
    paintDial(hitP, dialFillRef.current, dialKnobRef.current, phaseRefs.current, dialSvgRef.current);
  };
  const onDialUp = () => {
    dialDragRef.current = false;
  };
  const onDialKey = (e: React.KeyboardEvent) => {
    if (!['ArrowRight', 'ArrowUp', 'ArrowLeft', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const cur = dialManualRef.current?.p ?? dialScrollPRef.current;
    const dir = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? 1 : -1;
    grabDial(clamp(cur + dir / DIAL_PHASES.length, 0, 1));
  };

  // Hand-drawn scrollbar: drag the thumb, or press anywhere on the rail to jump there.
  const scrollRange = () => {
    const h = window.innerHeight;
    const doc = document.documentElement.scrollHeight - h;
    const thumbH = scrollThumbRef.current?.offsetHeight ?? SCROLL_THUMB_MIN;
    return { doc, travel: Math.max(h - thumbH, 1), thumbH };
  };
  const onBarDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const { doc, travel, thumbH } = scrollRange();
    if (e.target !== scrollThumbRef.current) {
      const p = clamp((e.clientY - thumbH / 2) / travel, 0, 1);
      window.scrollTo({ top: p * doc, behavior: 'auto' });
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* Pointer can already be gone; the jump still happened. */
    }
    scrollDragRef.current = { y: e.clientY, scroll: window.scrollY };
  };
  const onBarMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = scrollDragRef.current;
    if (!drag) return;
    const { doc, travel } = scrollRange();
    window.scrollTo({ top: drag.scroll + ((e.clientY - drag.y) / travel) * doc, behavior: 'auto' });
  };
  const onBarUp = () => {
    scrollDragRef.current = null;
  };

  const moreInfo = (key: LandingVideoKey) => (
    <div className="vl-more-slot" data-more>
      <button type="button" className="btn-gold vl-more-btn" onClick={() => setVideo(VIDEOS[key])}>
        <Play size={13} strokeWidth={2.4} fill="#fff" />
        Ver más información acerca de esto
      </button>
    </div>
  );

  const owner = path === 'owner';

  return (
    <div className="ventaje-landing font-montserrat" lang="es-MX" data-active-beat={activeBeat}>
      {/* Constellation lines — drift on scroll; lens bg is in App */}
      <div className="vl-bg-vignette" aria-hidden />
      <svg className="vl-bg-detail" ref={bgDetailRef} aria-hidden>
        <g stroke="rgba(255,255,255,0.06)" strokeWidth="1">
          <line x1="-100" y1="420" x2="1700" y2="-180" />
          <line x1="-100" y1="780" x2="1700" y2="140" />
          <line x1="-100" y1="1140" x2="1700" y2="480" />
        </g>
        <g stroke="rgba(255,255,255,0.10)" strokeWidth="0.7" fill="none">
          <polyline points="180,140 420,320 640,220 760,90" />
          <polyline points="980,260 1240,150 1380,430" />
          <polyline points="310,640 640,520 880,760 1120,600" />
          <polyline points="420,320 310,640" />
          <polyline points="1120,600 1380,430" />
        </g>
        <g fill="rgba(255,255,255,0.4)">
          {[
            [180, 140],
            [420, 320],
            [640, 220],
            [760, 90],
            [980, 260],
            [1240, 150],
            [1380, 430],
            [310, 640],
            [640, 520],
            [880, 760],
            [1120, 600],
          ].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r={1.2} />
          ))}
        </g>
      </svg>

      {/* Progress thread — white glow, like the app's edge scrollbars */}
      {/* [SPACING] Fixed bar on the left edge; its fill height is set from the scroll loop (threadFillRef). Styles: .vl-thread* in landing.css. */}
      <div className="vl-thread" aria-hidden>
        <div className="vl-thread-track" />
        <div className="vl-thread-fill" ref={threadFillRef} />
      </div>

      <div
        className="vl-scrollbar"
        aria-hidden
        onPointerDown={onBarDown}
        onPointerMove={onBarMove}
        onPointerUp={onBarUp}
        onPointerCancel={onBarUp}
      >
        <div className="vl-scrollbar-thumb" ref={scrollThumbRef} />
      </div>

      <header className={`vl-topbar${topSolid ? ' solid' : ''}`}>
        <span className="vl-logo">Ventaje</span>
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            className="btn-gold hidden sm:inline-flex items-center justify-center min-h-[44px] px-[22px] rounded-[10px] text-xs font-bold uppercase tracking-[0.1em]"
            onClick={() => navigate('/onboarding')}
          >
            Empezar
          </button>
          <button
            type="button"
            className="vl-burger"
            aria-label="Menú"
            onClick={() => openMenu()}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      <LandingMenu
        open={menuOpen}
        lang={lang}
        onClose={closeMenu}
        onLang={setLang}
      />

      {/* 1 · Hero */}
      <section
        className="vl-hero"
        id="top"
        data-beat="Inicio"
        ref={(el) => {
          beatRefs.current[0] = el;
        }}
      >
        <div className="vl-wrap-hero vl-hero-grid">
          <div className="vl-reveal">
            <h1 className="vl-h1">
              Dale la <b>ventaja</b> a tus ventas.
            </h1>
            <p className="vl-lead">
              El CRM con IA hecho para <span className="vl-em">inmobiliarias</span>,{' '}
              <span className="vl-em">brokers</span> y <span className="vl-em">desarrolladoras</span>.
              Atiende a cada lead, de la primera llamada al cierre, sin contratar a nadie más.
            </p>
            <div className="vl-hero-cta">
              <button
                type="button"
                className="btn-gold inline-flex items-center justify-center min-h-[64px] px-9 rounded-xl text-[16px] font-bold uppercase tracking-[0.1em]"
                onClick={() => go('#fork')}
              >
                Encuentra tu ventaja ↓
              </button>
              <button type="button" className="vl-btn-txt vl-btn-frost" onClick={() => setVideo(VIDEOS.hero)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
                  <polygon points="7,4 20,12 7,20" />
                </svg>
                Mira cómo funciona
              </button>
            </div>
          </div>
          <div className="vl-reveal">
            <LandingHeroPair />
          </div>
        </div>
      </section>

      {/* 2 · Fork */}
      <section
        className="vl-pin vl-pin-soft vl-pin-fork"
        id="fork"
        data-beat="¿Quién eres?"
        ref={(el) => {
          beatRefs.current[1] = el;
          forkSecRef.current = el;
        }}
      >
        <div className="vl-pin-inner">
        <div className="vl-pin-body exp-landing-progressive-glass vl-glass-band" data-water-cast="">
        <div className="vl-wrap vl-reveal">
          <p className="vl-kicker">Para empezar</p>
          <h2 className="vl-h2">
            Primero, <b>¿quién eres?</b>
          </h2>
          <div className="vl-fork">
            <button
              type="button"
              className={`vl-card${path === 'owner' ? ' vl-selected' : ''}`}
              aria-pressed={path === 'owner'}
              onPointerEnter={() => setPath('owner')}
              onClick={() => {
                setPath('owner');
                go('#c1');
              }}
            >
              <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="#A9E0F4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="8" r="3" />
                <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
                <path d="M16 11a3 3 0 1 0-2-5.2" />
                <path d="M18 20c0-2.2-.8-4-2-5.2" />
              </svg>
              <div className="vl-card-title">Dirijo un equipo de ventas</div>
              <p className="vl-tag" style={{ margin: 0 }}>
                Dueño · gerente
              </p>
            </button>
            <button
              type="button"
              className={`vl-card${path === 'agent' ? ' vl-selected' : ''}`}
              aria-pressed={path === 'agent'}
              onPointerEnter={() => setPath('agent')}
              onClick={() => {
                setPath('agent');
                go('#c1');
              }}
            >
              <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="#A9E0F4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="3.2" />
                <path d="M5 20c0-3.6 3.1-6.5 7-6.5s7 2.9 7 6.5" />
              </svg>
              <div className="vl-card-title">Soy asesor</div>
              <p className="vl-tag" style={{ margin: 0 }}>
                Vendo por mi cuenta o en equipo
              </p>
            </button>
          </div>
          <button
            type="button"
            className="vl-small-link"
            onClick={() => navigate('/socios')}
          >
            ¿Agencia de marketing o consultor que trabaja con inmobiliarias? Conoce el programa de
            socios →
          </button>
        </div>
        </div>
        </div>
      </section>

      {/* 3 · Contrast 1 */}
      <section
        className="vl-pin"
        id="c1"
        data-pin
        data-beat="¿Vendes o capturas?"
        ref={(el) => {
          beatRefs.current[2] = el;
          c1Ref.current = el;
          pinRefs.current[0] = el;
        }}
      >
        <div className="vl-pin-inner">
          <div className="vl-pin-body">
          <div className="vl-pin-head">
            <p className="vl-kicker">El día a día</p>
            <h2 className="vl-h2">
              {owner ? (
                <>
                  ¿Tu equipo vende… <b>o captura datos?</b>
                </>
              ) : (
                <>
                  ¿Vendes… <b>o capturas datos?</b>
                </>
              )}
            </h2>
          </div>
          <div className="vl-stage">
            <div className="vl-side no" data-no>
              <div className="vl-card vl-card-bare">
                <span className="vl-tag vl-flag vl-flag-no">Hoy</span>
                <p>
                  {owner
                    ? 'Teléfono, WhatsApp, CRM, copiar, pegar. Lead por lead. Se les va el día documentando.'
                    : 'Contestas, copias, pegas, llenas la ficha. Y otra vez con el siguiente lead.'}
                </p>
                <ContrastVisual section="c1" side="no" />
              </div>
            </div>
            <div className="vl-side si" data-si>
              <div className="vl-card">
                <span className="vl-tag vl-flag vl-flag-si">Con Ventaje</span>
                <p>
                  <b>Tú vendes. Ventaje documenta.</b>{' '}
                  {owner
                    ? 'Mientras tu equipo atiende, todo queda registrado solo.'
                    : 'Mientras platicas, la ficha se llena sola.'}
                </p>
                <ContrastVisual section="c1" side="si" />
              </div>
            </div>
            <div className="vl-stage-spacer" />
            {moreInfo('c1')}
          </div>
          </div>
        </div>
      </section>

      {/* 4 · Statement */}
      {/* [SPACING] This black band has 140px of padding above and below (inline style). Text is centered. */}
      <section
        className="exp-landing-black-glass vl-black-band"
        id="statement"
        data-beat="No te faltan leads"
        data-water-cast=""
        style={{ paddingTop: 140, paddingBottom: 140 }}
        ref={(el) => {
          beatRefs.current[3] = el;
          statementRef.current = el;
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <h2 className="vl-h-black vl-words">
            {['No', 'te', 'faltan', 'leads.'].map((w, i) => (
              <React.Fragment key={`a${i}`}>
                <span
                  ref={(el) => {
                    wordSpansRef.current[i] = el;
                  }}
                >
                  {w}
                </span>{' '}
              </React.Fragment>
            ))}
            <br />
            {['Te', 'falta', 'tiempo', 'para', 'atenderlos', 'a', 'todos.'].map((w, i) => (
              <React.Fragment key={`b${i}`}>
                <span
                  className="key"
                  ref={(el) => {
                    wordSpansRef.current[4 + i] = el;
                  }}
                >
                  {w}
                </span>{' '}
              </React.Fragment>
            ))}
          </h2>
        </div>
      </section>

      {/* 5 · AI contrast */}
      <section
        className="vl-pin"
        id="ia"
        data-pin
        data-beat="La IA bien usada"
        ref={(el) => {
          beatRefs.current[4] = el;
          pinRefs.current[1] = el;
        }}
      >
        <div className="vl-pin-inner">
          <div className="vl-pin-body">
          <div className="vl-pin-head">
            <p className="vl-kicker">La diferencia</p>
            <h2 className="vl-h2">
              Todos ya usan IA. <b>Casi nadie la usa bien.</b>
            </h2>
          </div>
          <div className="vl-stage">
            <div className="vl-side no" data-no>
              <div className="vl-card vl-card-bare">
                <span className="vl-tag vl-flag vl-flag-no">Los demás</span>
                <p>
                  Le pegaron IA a un CRM viejo. Estorba, contesta raro y nadie sabe dónde ponerla.
                </p>
                <ContrastVisual section="ia" side="no" />
              </div>
            </div>
            <div className="vl-side si" data-si>
              <div className="vl-card">
                <span className="vl-tag vl-flag vl-flag-si">Ventaje</span>
                <p>
                  <b>Ventaje se construyó con la IA adentro,</b> desde el primer día.
                </p>
                <ContrastVisual section="ia" side="si" />
              </div>
            </div>
            <div className="vl-stage-spacer" />
            {moreInfo('ia')}
          </div>
          </div>
        </div>
      </section>

      {/* Dial + audio */}
      {/* [ANIMATION] DIAL (beat 5b). A soft pin: #dial is 480vh tall (landing.css). The body eases to a full stop in the middle
          (movePinBody ease 0.32, floor 0) so the whole cycle can be watched. The needle stays on "Secretaria" until pin progress
          0.28 (DIAL_SWEEP_START), then turns across the long hold and reaches the last role ("Socia") by 0.64 (DIAL_SWEEP_END).
          Pointer: drag or tap the arc to take over by hand; a real scroll of more than 0.02 progress hands control back. Arrow keys
          step one role (a fifth of the arc). Arc: "M30 150 A120 120 0 0 1 270 150", 14px stroke, round caps, soft glow filter,
          gradient #8fcfe6 -> #bfe9f7 (55%) -> #fff. The five role blocks sit inside the arc and cross-fade (class `on`). On mobile or
          reduced motion (not pinned), the needle simply fills as the section enters. */}
      <section
        className="vl-pin vl-pin-soft"
        id="dial"
        data-beat="La IA en su punto"
        ref={(el) => {
          beatRefs.current[5] = el;
          dialSecRef.current = el;
        }}
      >
        <div className="vl-pin-inner">
        <div className="vl-pin-body exp-landing-progressive-glass vl-glass-band" data-water-cast="">
        <div className="vl-wrap" style={{ textAlign: 'center' }}>
          <h2 className="vl-h2 vl-reveal" style={{ marginTop: 0 }}>
            La IA <b>en su punto.</b>
            <br />
            Ni de más, ni de menos.
          </h2>
          <p className="vl-lead vl-reveal" style={{ margin: '12px auto 18px' }}>
            Trabaja dentro de tu proceso y habla con tu voz… hasta en las llamadas.
          </p>
          <div className="vl-dial-wrap">
            <svg
              ref={dialSvgRef}
              className="vl-dial-svg"
              viewBox="0 0 300 170"
              width="100%"
              role="slider"
              aria-label="Perilla de automatización, de mínimo a máximo"
              aria-valuemin={0}
              aria-valuemax={100}
              tabIndex={0}
              onPointerDown={onDialDown}
              onPointerMove={onDialMove}
              onPointerUp={onDialUp}
              onPointerCancel={onDialUp}
              onKeyDown={onDialKey}
            >
              <defs>
                <filter id="vl-gl" x="-60%" y="-60%" width="220%" height="220%">
                  <feGaussianBlur stdDeviation="6" result="b" />
                  <feMerge>
                    <feMergeNode in="b" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <linearGradient id="vl-dial-grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#8fcfe6" />
                  <stop offset="0.55" stopColor="#bfe9f7" />
                  <stop offset="1" stopColor="#ffffff" />
                </linearGradient>
              </defs>
              <path
                d="M30 150 A120 120 0 0 1 270 150"
                fill="none"
                stroke="rgba(255,255,255,0.14)"
                strokeWidth="14"
                strokeLinecap="round"
              />
              <path
                ref={dialFillRef}
                d="M30 150 A120 120 0 0 1 270 150"
                fill="none"
                stroke="url(#vl-dial-grad)"
                strokeWidth="14"
                strokeLinecap="round"
                filter="url(#vl-gl)"
                strokeDasharray={DIAL_LEN}
                strokeDashoffset={DIAL_LEN}
              />
              <circle ref={dialKnobRef} cx="30" cy="150" r="12" fill="#fff" filter="url(#vl-gl)" />
            </svg>
            {/* Phases sit inside the arc and cross-fade as the dial turns */}
            <div className="vl-dial-phases" aria-live="polite">
              {DIAL_PHASES.map((ph, i) => {
                const Icon = ph.icon;
                return (
                  <div
                    key={ph.role}
                    className={`vl-dial-phase${i === 0 ? ' on' : ''}`}
                    ref={(el) => {
                      phaseRefs.current[i] = el;
                    }}
                  >
                    <Icon size={32} strokeWidth={1.5} />
                    <strong>{ph.role}</strong>
                    <ul>
                      {ph.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <div className="vl-dial-labels">
              <span className="vl-tag">Mínimo</span>
              <span className="vl-tag">Máximo</span>
            </div>
          </div>
          {/* [UI-BACKEND-TODO] Real call clip: set VIDEOS.call.src in LandingVideoModal. */}
          <div className="vl-wave vl-reveal" role="group" aria-label="Llamada real de la IA">
            <button
              type="button"
              className="vl-play"
              aria-label="Ver la llamada"
              onClick={() => setVideo(VIDEOS.call)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff">
                <polygon points="7,4 20,12 7,20" />
              </svg>
            </button>
            <div className="vl-wave-bars" aria-hidden>
              {Array.from({ length: 40 }, (_, i) => (
                <i key={i} style={{ ['--i' as string]: i }} />
              ))}
            </div>
            <span className="vl-wave-label">
              Llamada real · <b>0:30</b>
              <small>Este no soy yo. Es la IA, con mi voz.</small>
            </span>
          </div>
        </div>
        </div>
        </div>
      </section>

      {/* 6 · Money */}
      <section
        className="vl-pin exp-landing-black-glass"
        id="money"
        data-pin
        data-last
        data-beat="La cuenta"
        data-water-cast=""
        style={{ boxShadow: '0 -60px 120px rgba(6,16,21,0.7),0 60px 120px rgba(6,16,21,0.7)' }}
        ref={(el) => {
          beatRefs.current[6] = el;
          pinRefs.current[2] = el;
        }}
      >
        <div className="vl-pin-inner">
          <div className="vl-pin-body">
          <div className="vl-pin-head">
            <p className="vl-kicker">La cuenta</p>
            <h2 className="vl-h2">
              {owner ? (
                <>
                  ¿Diez asesores para cubrir todos tus leads…{' '}
                  <b>o tu mejor cerrador, sin el trabajo pesado?</b>
                </>
              ) : (
                <>
                  ¿Perseguir leads hasta la noche… <b>o entrar solo cuando hay que cerrar?</b>
                </>
              )}
            </h2>
          </div>
          <div className="vl-stage">
            <div className="vl-side no" data-no>
              <div className="vl-card vl-card-bare">
                <span className="vl-tag vl-flag vl-flag-no">Hoy</span>
                <p>
                  {owner
                    ? 'Diez sueldos, diez comisiones, y aun así se enfrían leads.'
                    : 'Seguimientos, recordatorios, mensajes que se te olvidan.'}
                </p>
                <ContrastVisual section="money" side="no" />
              </div>
            </div>
            <div className="vl-side si" data-si>
              <div className="vl-card">
                <span className="vl-tag vl-flag vl-flag-si">Con Ventaje</span>
                <p>
                  <b>La IA da el seguimiento.</b>{' '}
                  {owner
                    ? 'Tu mejor cerrador solo cierra. Menos carga, menor costo por venta.'
                    : 'Tú entras cuando hay que cerrar.'}
                </p>
                <ContrastVisual section="money" side="si" />
              </div>
            </div>
            <div className="vl-stage-spacer" />
            {moreInfo('money')}
          </div>
          </div>
        </div>
      </section>

      {/* 7 · Consequence */}
      <section
        className="exp-landing-black-glass vl-black-band"
        id="conseq"
        data-beat="Un año más"
        data-water-cast=""
        ref={(el) => {
          beatRefs.current[7] = el;
        }}
      >
        <div className={`vl-wrap vl-conseq${conseqOn ? ' on' : ''}`} ref={conseqRef}>
          <h2 className="vl-h-black" style={{ margin: '0 0 34px' }}>
            Ahora imagina{' '}
            <b style={{ color: 'var(--vl-blue)' }}>seguir igual un año más.</b>
          </h2>
          <p>Los mismos leads enfriándose.</p>
          <p>El mismo equipo sin tiempo.</p>
          <p>La misma pregunta cada lunes: ¿cuántos se nos fueron?</p>
        </div>
      </section>

      {/* 8 · Close */}
      {/* [HANDOFF] Owner and agent see two different versions of this section. Owner: pinned, three typed promises, button "Empezar →"
          scrolls to #cuestionario. Agent: plain glass band, "Quiero recomendarlo →" goes to /socios (the partner page that will be swapped).
          Both also show a small link to /socios. */}
      <section
        className={
          owner
            ? 'vl-pin vl-pin-close'
            : 'exp-landing-progressive-glass vl-glass-band vl-band-full'
        }
        id="cierre"
        data-beat="Sin riesgo"
        data-water-cast={owner ? undefined : ''}
        ref={(el) => {
          beatRefs.current[8] = el;
          closeRef.current = el;
        }}
      >
        <div className={owner ? 'vl-pin-inner' : 'vl-contents'}>
        <div
          className={
            owner
              ? 'vl-pin-body exp-landing-progressive-glass vl-glass-band vl-band-full'
              : 'vl-contents'
          }
          data-water-cast={owner ? '' : undefined}
        >
        <div className="vl-wrap vl-reveal">
          <p className="vl-kicker">Sin riesgo</p>
          <h2 className="vl-h2">
            {owner ? (
              <>
                Suena a que <b>necesitas Ventaje.</b>
              </>
            ) : (
              <>
                ¿Tu empresa <b>todavía no usa Ventaje?</b>
              </>
            )}
          </h2>
          <p className="vl-lead">
            {owner
              ? 'Descubre si es para tu equipo. Sin riesgo:'
              : 'Recomiéndalo y gana comisión cada mes que sigan con nosotros.'}
          </p>
          {owner && (
            <div className="vl-steps">
              {CLOSE_STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div className="vl-step" key={s.title}>
                    <span className="n">0{i + 1}</span>
                    <div className="vl-step-ico">
                      <Icon size={30} strokeWidth={1.5} />
                    </div>
                    <div className="vl-step-text">
                      {/* The full copy, in transparent text: it holds the step at its final height while the visible copy types, and
                          it is the copy a screen reader reads; the typed copy is aria-hidden (empty until the pin is scrolled
                          through). QA-F10, WU-164 D9. */}
                      <div className="vl-step-ghost">
                        <div className="vl-card-title">{s.title}</div>
                        <p style={{ margin: 0 }}>{s.body}</p>
                      </div>
                      <div aria-hidden>
                        <div className="vl-card-title">
                          <span data-step-title>{s.title}</span>
                          <span className="vl-caret" data-caret="title" hidden />
                        </div>
                        <p style={{ margin: 0, color: 'var(--vl-sec)' }}>
                          <span data-step-body>{s.body}</span>
                          <span className="vl-caret" data-caret="body" hidden />
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className={owner ? 'vl-empezar' : undefined} style={{ marginTop: 38 }}>
            <button
              type="button"
              className="btn-gold inline-flex items-center justify-center min-h-[54px] px-7 rounded-[10px] text-[13px] font-bold uppercase tracking-[0.1em]"
              onClick={() => {
                if (owner) go('#cuestionario');
                else navigate('/socios');
              }}
            >
              {owner ? 'Empezar →' : 'Quiero recomendarlo →'}
            </button>
          </div>
          <button
            type="button"
            className="vl-small-link"
            onClick={() => navigate('/socios')}
          >
            {owner
              ? '¿No es para ti, pero conoces a alguien? Gana recomendándolo →'
              : '¿Trabajas por tu cuenta? Escríbenos →'}
            </button>
        </div>
        </div>
        </div>
      </section>

      {/* 9 · First setup question. Continue opens the next one at /onboarding. */}
      {/* [HANDOFF] LandingPathStep saves the choice into localStorage ventaje_draft_v3 (only `path`) and sets sessionStorage
          ventaje_s1_done, so the builder skips its own first screen. [VERIFY] The kicker below says "Paso 1 de 5" (left over from the old
          five-step questionnaire, LandingQuiz.tsx), but the real onboarding shows "Paso X de 11" or "de 12" (10 / 11 when this question
          was already answered here). Decide what the count should say. [URL-RENAME] id cuestionario -> quiz. */}
      <section
        className="vl-pin vl-pin-quiz"
        id="cuestionario"
        data-beat="Tu cuenta"
        ref={(el) => {
          beatRefs.current[9] = el;
          qSecRef.current = el;
        }}
      >
        <div className="vl-pin-inner">
          <div className="vl-wrap">
            <p className="vl-kicker">Paso 1</p>
            <h2 className="vl-h2">
              Cuéntanos <b>cómo trabajas.</b>
            </h2>
            <LandingPathStep onContinue={() => navigate('/onboarding')} />
          </div>
        </div>
      </section>

      <footer className="vl-footer">
        <div className="vl-foot-grid">
          <span className="vl-logo">Ventaje</span>
          <span>Mérida, Yucatán · 2026</span>
          <button type="button" className="vl-small-link" style={{ margin: 0 }} onClick={() => openMenu()}>
            Menú · Socios · Preguntas · Contacto
          </button>
        </div>
      </footer>

      {/* [ANIMATION] Floating "¿Dudas? Escríbenos." bubble. `on` is set by the scroll loop (see the waOn rule). [URL-RENAME] /contacto -> /contact */}
      <button type="button" className={`vl-wa${waOn ? ' on' : ''}`} onClick={() => navigate('/contacto')}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A9E0F4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.4 8.4 0 0 1-12.6 7.3L3 20.5l1.8-5.2A8.5 8.5 0 1 1 21 11.5z" />
        </svg>
        ¿Dudas? Escríbenos.
      </button>

      <LandingVideoModal video={video} onClose={closeVideo} />
    </div>
  );
};

export default LandingPage;
